import { BusElement } from '../element/element-bus.js'
import { DOT_TYPE_MAP, DEFAULT_LABELS } from './dot-parser.js'

/** 元件 type -> dot 关键字 */
const TYPE_TO_DOT = Object.fromEntries(
  Object.entries(DOT_TYPE_MAP).map(([dotType, ctorType]) => [ctorType, dotType]),
)

/**
 * 将拓扑图序列化为 DOT 语法字符串
 */
export class DotSerializer {
  /**
   * @param {import('../topology.js').Topology} topology
   */
  constructor(topology) {
    this.topology = topology
  }

  /**
   * @returns {string}
   */
  serialize() {
    const graph = this.topology.storage.graph
    const buses = this.topology.storage.displayElement
      .filter((elem) => elem instanceof BusElement)
      .sort((a, b) => a.cx - b.cx || a.cy - b.cy)

    if (!buses.length) {
      return ''
    }

    const lines = []
    for (const bus of buses) {
      const pathStepsList = this.collectPathSteps(bus, graph)
      for (const steps of pathStepsList) {
        const line = steps
          .map((step) => step.join(','))
          .join(' -> ')
        if (line) {
          lines.push(line)
        }
      }
    }

    return lines.join('\n')
  }

  /**
   * 从根节点收集路径步骤（每步为并列节点 token 列表）
   * @param {import('../graph-element.js').GraphElement} root
   * @param {import('../graph.js').Graph} graph
   * @returns {string[][][]}
   */
  collectPathSteps(root, graph) {
    /** @type {string[][][]} */
    const results = []

    /**
     * @param {import('../graph-element.js').GraphElement} node
     * @param {string[][]} prefix
     */
    const walk = (node, prefix) => {
      const children = this.getChildren(node, graph)

      if (!children.length) {
        results.push(prefix)
        return
      }

      const childTokens = children
        .map((child) => this.elemToToken(child))
        .filter(Boolean)

      const stepTokens = this.compressStep(childTokens)
      const nextPrefix = [...prefix, stepTokens]

      const allLeaves = children.every((child) => !this.getChildren(child, graph).length)
      if (allLeaves) {
        results.push(nextPrefix)
        return
      }

      for (const child of children) {
        const token = this.elemToToken(child)
        if (!token) continue
        walk(child, [...prefix, [token]])
      }
    }

    const rootToken = this.elemToToken(root)
    if (rootToken) {
      walk(root, [[rootToken]])
    }

    return results
  }

  /**
   * @param {import('../graph-element.js').GraphElement} node
   * @param {import('../graph.js').Graph} graph
   */
  getChildren(node, graph) {
    const list = graph.adjList.get(node) || []
    return list
      .map((vertex) => vertex.node)
      .filter((child) => child && child !== node)
      .sort((a, b) => a.cx - b.cx || a.cy - b.cy)
  }

  /**
   * @param {import('../graph-element.js').GraphElement} elem
   * @returns {string|null}
   */
  elemToToken(elem) {
    const dotType = TYPE_TO_DOT[elem.type]
    if (!dotType) {
      return null
    }

    const label = elem.label?.attrs?.text || elem.name || ''

    const elinkMatch = /^(.+)#eLink$/.exec(label)
    if (dotType === 'elink' && elinkMatch) {
      return `elink(${elinkMatch[1]})`
    }

    const eblockMatch = /^(.+)#eBlock$/.exec(label)
    if (dotType === 'eblock' && eblockMatch) {
      return `eblock(${eblockMatch[1]})`
    }

    if (label && label !== DEFAULT_LABELS[dotType]) {
      if (dotType === 'elink' || dotType === 'eblock' || dotType === 'pcc') {
        return `${dotType}(${label})`
      }
    }

    return dotType
  }

  /**
   * 压缩同级节点，如多个连续编号的 eblock 合并为 eblock*N
   * @param {string[]} tokens
   * @returns {string[]}
   */
  compressStep(tokens) {
    if (tokens.length <= 1) {
      return tokens
    }

    const numberedEblocks = tokens.every((token) => /^eblock\(\d+\)$/.test(token))
    if (numberedEblocks) {
      const nums = tokens.map((token) => Number(token.match(/\d+/)?.[0]))
      const sorted = [...nums].sort((a, b) => a - b)
      const isSequential = sorted.every((num, index) => num === index + 1)
      if (isSequential) {
        return [`eblock*${tokens.length}`]
      }
    }

    const anonymousEblocks = tokens.every((token) => token === 'eblock')
    if (anonymousEblocks) {
      return [`eblock*${tokens.length}`]
    }

    return tokens
  }
}
